/**
 * Quill 에디터 클립보드/붙여넣기 관련 유틸리티
 *
 * - sanitizeNbsp: 표시(display) 시 &nbsp; → 일반 공백 변환
 * - isHtmlEmpty: HTML 문자열이 빈 내용인지 확인
 * - preserveConsecutiveSpaces: 연속 공백을 공백+nbsp 교차 패턴으로 보존
 * - preprocessPasteHtml: HTML 붙여넣기 시 공백 보존 전처리
 * - createPasteHandler: Quill 에디터용 DOM paste 이벤트 핸들러 팩토리
 */

/**
 * &nbsp; 엔티티를 일반 공백으로 변환 (display 컴포넌트용)
 * whitespace-pre-wrap CSS와 함께 사용하면 정상 렌더링됨
 */
export const sanitizeNbsp = (html?: string | null): string =>
  (html || "").replace(/&nbsp;/g, " ");

/**
 * HTML 문자열이 실질적으로 비어있는지 확인
 * 태그와 &nbsp;를 제거한 후 텍스트가 없으면 빈 것으로 판단
 */
export const isHtmlEmpty = (html?: string | null): boolean => {
  if (!html) return true;
  const text = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();
  return text.length === 0;
};

/**
 * 연속 공백(2개 이상)을 공백+\u00a0 교차 패턴으로 변환
 * Quill의 `/ {2,}/g` 축소를 우회하기 위함
 *
 * 예: "a    b" → "a \u00a0 \u00a0b"
 */
export const preserveConsecutiveSpaces = (text: string): string =>
  text.replace(/ {2,}/g, (match) => {
    let result = "";
    for (let i = 0; i < match.length; i++) {
      result += i % 2 === 0 ? "\u00a0" : " ";
    }
    return result;
  });

/**
 * 저장 전 HTML 텍스트 노드의 선행/후행/연속 일반 공백을 보존 처리.
 *
 * Quill은 HTML을 다시 로드할 때 블록 시작 일반 공백을 정규화할 수 있으므로,
 * 사용자가 직접 입력한 공백을 NBSP가 섞인 형태로 저장해 재진입 시에도 유지한다.
 */
export const preserveHtmlSpaces = (html?: string | null): string => {
  if (!html) return "";

  const preserveTextSpaces = (text: string): string => {
    const normalized = text.replace(/\u00a0/g, " ");
    const leadingSpaces = normalized.match(/^ +/)?.[0] || "";
    const trailingSpaces = normalized.match(/ +$/)?.[0] || "";
    const bodyStart = leadingSpaces.length;
    const bodyEnd = normalized.length - trailingSpaces.length;
    const body = normalized.slice(bodyStart, bodyEnd);

    return (
      "\u00a0".repeat(leadingSpaces.length) +
      preserveConsecutiveSpaces(body) +
      "\u00a0".repeat(trailingSpaces.length)
    );
  };

  if (typeof document === "undefined") {
    return html
      .replace(/(^|>)( +)/g, (_, prefix: string, spaces: string) =>
        prefix + preserveConsecutiveSpaces(spaces)
      )
      .replace(/( +)(<|$)/g, (match: string, spaces: string, suffix: string) =>
        preserveConsecutiveSpaces(spaces) + suffix
      )
      .replace(/ {2,}/g, preserveConsecutiveSpaces);
  }

  const template = document.createElement("template");
  template.innerHTML = html;

  const walker = document.createTreeWalker(
    template.content,
    NodeFilter.SHOW_TEXT
  );

  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((node) => {
    node.nodeValue = preserveTextSpaces(node.nodeValue || "");
  });

  return template.innerHTML;
};

export const QUILL_TEXT_NODE = 3;

type QuillSelection = {
  index: number;
  length: number;
};

type QuillDeltaOp = {
  insert?: unknown;
};

type QuillDeltaLike = {
  ops?: QuillDeltaOp[];
};

type QuillDeltaConstructor = new () => {
  insert: (text: string) => unknown;
};

type QuillClipboardEditor = {
  getSelection: () => QuillSelection | null;
  insertEmbed: (index: number, type: string, value: string) => void;
  setSelection: (index: number) => void;
  deleteText: (index: number, length: number) => void;
  clipboard: {
    dangerouslyPasteHTML: (
      index: number,
      html: string,
      source: "user"
    ) => void;
  };
};

const ELEMENT_NODE = 1;
const LINE_TAGS = new Set([
  "address",
  "article",
  "blockquote",
  "dd",
  "div",
  "dl",
  "dt",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "li",
  "main",
  "nav",
  "ol",
  "output",
  "p",
  "pre",
  "section",
  "table",
  "td",
  "tr",
  "ul",
]);

const isElementNode = (node: Node | null): node is Element =>
  !!node && node.nodeType === ELEMENT_NODE;

const getPreviousMeaningfulSibling = (node: Node): Node | null => {
  let sibling = node.previousSibling;
  while (
    sibling &&
    sibling.nodeType === QUILL_TEXT_NODE &&
    sibling.textContent === ""
  ) {
    sibling = sibling.previousSibling;
  }
  return sibling;
};

const isLineElement = (node: Node | null): boolean =>
  isElementNode(node) && LINE_TAGS.has(node.tagName.toLowerCase());

const startsAtLineBoundary = (node: Node): boolean => {
  let current: Node = node;

  while (current.parentNode) {
    const previousSibling = getPreviousMeaningfulSibling(current);
    if (previousSibling) {
      return isElementNode(previousSibling) && previousSibling.tagName === "BR";
    }

    const parent = current.parentNode;
    if (isLineElement(parent)) {
      return true;
    }

    current = parent;
  }

  return false;
};

const getDeltaText = (delta: QuillDeltaLike): string =>
  (delta.ops || [])
    .map((op) => (typeof op.insert === "string" ? op.insert : ""))
    .join("");

export const preserveLineStartSpacesMatcher = (
  node: Node,
  delta: QuillDeltaLike
) => {
  const rawText = node.textContent || "";
  const normalizedText = rawText.replace(/\u00a0/g, " ");
  const leadingSpaces = normalizedText.match(/^ +/)?.[0] || "";

  if (!leadingSpaces || !startsAtLineBoundary(node)) {
    return delta;
  }

  const Delta = (delta as { constructor: unknown })
    .constructor as QuillDeltaConstructor;
  const defaultText = getDeltaText(delta);
  const defaultTextWithoutLeadingSpaces = defaultText.replace(/^ +/, "");

  return new Delta().insert(
    "\u00a0".repeat(leadingSpaces.length) + defaultTextWithoutLeadingSpaces
  );
};

/**
 * HTML 문자열에서 연속 공백 패턴을 보존 처리
 *
 * Quill의 matchText()가 연속 일반 공백을 1개로 축소하므로,
 * 연속 공백을 공백+&nbsp; 교차 패턴으로 변환하여 보존.
 *
 * 단독 &nbsp;는 변환하지 않음 — Quill이 모든 공백을 &nbsp;로 저장하며,
 * display 시 sanitizeNbsp()가 처리.
 */
export const preprocessPasteHtml = (html: string): string => {
  // 1) 유니코드 \u00a0 → &nbsp; 엔티티로 통일
  let result = html.replace(/\u00a0/g, "&nbsp;");

  // 2) 연속 일반 공백(2개 이상)을 교차 패턴으로 변환
  result = result.replace(/ {2,}/g, (match) => {
    let out = "";
    for (let i = 0; i < match.length; i++) {
      out += i % 2 === 0 ? " " : "&nbsp;";
    }
    return out;
  });

  return result;
};

/**
 * plain text를 Quill에 삽입할 HTML로 변환
 * - 각 줄을 <p> 태그로 감싸 (Quill의 네이티브 Enter 동작과 일치)
 * - 빈 줄은 <p><br></p>로 변환 (Quill 엔터 2번과 동일)
 * - 연속 공백 → 교차 패턴 보존
 * - HTML 특수문자 이스케이프
 */
const plainTextToHtml = (text: string): string => {
  const lines = text.split("\n");

  return lines
    .map((line) => {
      if (line.length === 0) {
        // 빈 줄 → Quill에서 엔터 2번 친 것과 동일
        return "<p><br></p>";
      }

      // HTML 특수문자 이스케이프
      let escaped = line
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

      // 연속 공백 보존 (공백+&nbsp; 교차)
      escaped = escaped.replace(/ {2,}/g, (match) => {
        let result = "";
        for (let i = 0; i < match.length; i++) {
          result += i % 2 === 0 ? " " : "&nbsp;";
        }
        return result;
      });

      return `<p>${escaped}</p>`;
    })
    .join("");
};

/**
 * Quill 에디터용 DOM paste 이벤트 핸들러 팩토리
 *
 * @param quill - Quill 에디터 인스턴스
 * @param editorName - 에디터 식별명 (디버그 로깅용)
 * @param onImagePaste - 이미지 붙여넣기 콜백. File을 받아 업로드 후 URL 반환
 */
export const createPasteHandler = (
  quill: QuillClipboardEditor,
  editorName: string,
  onImagePaste: (file: File) => Promise<string>
) => {
  return async (e: ClipboardEvent) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // 1. 이미지 붙여넣기 처리
    const items = clipboardData.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          console.log(
            `Image paste detected in ${editorName} editor - uploading...`
          );
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const file = items[i].getAsFile();
          if (file) {
            const imageUrl = await onImagePaste(file);
            const range = quill.getSelection();
            if (range) {
              quill.insertEmbed(range.index, "image", imageUrl);
              quill.setSelection(range.index + 1);
            }
            console.log(
              `Image uploaded and inserted in ${editorName} editor`
            );
          }
          return;
        }
      }
    }

    // 2. 텍스트/HTML 붙여넣기 - 공백 보존 처리
    e.preventDefault();
    e.stopPropagation();

    const htmlData = clipboardData.getData("text/html");
    const plainData = clipboardData.getData("text/plain");

    let htmlToInsert: string;

    if (htmlData) {
      // HTML 붙여넣기: 공백 보존 전처리 후 삽입
      htmlToInsert = preprocessPasteHtml(htmlData);
    } else if (plainData) {
      // Plain text 붙여넣기: HTML로 변환 (줄바꿈 + 공백 보존)
      htmlToInsert = plainTextToHtml(plainData);
    } else {
      return;
    }

    const range = quill.getSelection();
    if (range) {
      // 선택 영역이 있으면 삭제 후 삽입
      if (range.length > 0) {
        quill.deleteText(range.index, range.length);
      }
      quill.clipboard.dangerouslyPasteHTML(range.index, htmlToInsert, "user");
    }
  };
};
